import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import FrequentlyAskedQuestions, {
  FaqParagraph,
} from '@rover/components/src/FrequentlyAskedQuestions';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Link } from '@rover/kibble/core';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryConfigurationUrlsLocalizedLinkUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrlsLocalizedLinkUrls';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';

type DoggyDayCareServiceFaqsProps = {
  htmlRef?: React.RefObject<HTMLElement>;
  localizedLinkUrls: CountryConfigurationUrlsLocalizedLinkUrls;
  countryConfig: CountryConfiguration;
  locale: Locale;
  searchFilters: Partial<SearchFilters>;
};

const DoggyDayCareServiceFaqs: React.FC<DoggyDayCareServiceFaqsProps> = ({
  htmlRef,
  localizedLinkUrls: links,
  countryConfig,
  locale,
  searchFilters,
}) => {
  const { t } = useLingui();
  const { hideBackgroundCheckVerbiage } = countryConfig;

  const searchUrl = getSearchUrl(searchFilters, locale);
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="doggy-day-care-service-faqs"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Doggy day care near you</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>
          Give your dog a day of in-home play and socialization right in your neighborhood
        </Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`What does doggy day care include?`}>
          <>
            <FaqParagraph>
              <Trans>
                A typical day care day includes supervision, companionship, and same day pick up and
                drop off, while some also offer structured routines throughout the day. A structured
                routine might include scheduled play, rest periods, and feeding times. Ask the
                sitter for routine and activity specifics. You can customize the style of doggy day
                care you want when choosing day care in a sitter's home. When you{' '}
                <Link href={searchUrl}>search for day care sitters</Link>, you can also choose the
                option to filter for single-dog daycare, where your dog is the only pet present.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                <strong>Tip:</strong> If your dog prefers human-only companionship, check
                &quot;Doesn't own a dog&quot; and &quot;Only accepts one client at a time&quot; to
                find the matching sitters.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        {'findingASitter' in links && (
          <FrequentlyAskedQuestion question={t`Where can I find a doggy day care near me?`}>
            <>
              <FaqParagraph>
                <Trans>
                  Finding a local doggy day care is simple! Enter your zipcode into the address box
                  and sitters who match your settings will appear on the map and in the list view.
                  You can also zoom in or out on the map and refresh your search to see updated day
                  care profiles. For more details, visit our{' '}
                  <Link href={links.findingASitter}>Help Center</Link>.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  <strong>Tip:</strong> Need day care close to work?{' '}
                  <Link href={searchUrl}>Search for doggy day cares</Link> near your office for
                  convenient drop-off and pick-up.
                </Trans>
              </FaqParagraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`What is the best doggy day care for small breeds?`}>
          <FaqParagraph>
            <Trans>
              The best doggy day care for small breeds provide a safe, comfortable environment for
              small dogs. Look for sitters who offer separate play areas by dog size and energy
              level, or in-home day care with small dogs only. When vetting a doggy day care, ask
              the sitter about the number of dogs, their experience with different dog behaviors,
              and their approach to small dog care. For some small dogs, in-home day care is the
              perfect fit. Look find sitters whose &quot;can host&quot; section only lists dogs 0-15
              pounds and/or 16-40 pounds. Some sitters may also list the size and type of dogs they
              watch in their profiles.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={t`What is the best doggy day care for high-energy breeds?`}
        >
          <FaqParagraph>
            <Trans>
              For dogs with lots of energy, the best doggy day cares will offer a balance of
              structured play time and rest periods. Scheduled breaks are especially important for
              high energy dogs, helping them learn how to relax and preventing overstimulation.
              Depending on your dog's specific needs, you may also want to focus on a specific day
              care environment. Some dogs thrive in larger spaces or backyards, while others do best
              with a sitter who can provide enrichment activities or one-on-one attention.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion question={t`What is the best doggy day care for social dogs?`}>
          <>
            <FaqParagraph>
              <Trans>
                Doggy day care can be a great place for social dogs to play, interact, and build
                confidence around other dogs and people. When choosing a day care, ask the sitter
                about how they support positive socialization, such as supervised group play,
                rotating playgroups, and scheduled rest periods between activities.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                <strong>Tip:</strong> If your dog enjoys playing with other dogs,{' '}
                <Link href={searchUrl}>book doggy day care</Link> with a sitter who hosts multiple
                dogs on the same days you need care.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        {'isDoggyDayCareGoodForMyDog' in links && (
          <FrequentlyAskedQuestion question={t`Is it good for my dog to go to doggy day care?`}>
            <>
              <FaqParagraph>
                <Trans>
                  Doggy day care can be a{' '}
                  <Link href={links.isDoggyDayCareGoodForMyDog}>great option</Link> for dogs who
                  need companionship, mental stimulation, and safe supervision while you're away.
                  Rover is unique in offering different types of day care to fit dogs of all
                  personalities and needs.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  Traditional facility-based doggy day care provides these benefits in a structured,
                  supervised environment. However, some dogs may find busy facilities
                  overstimulating. For those pups, in-home day care with a sitter can be a better
                  fit—offering personalized attention, tailored activities, smaller play groups, and
                  a quieter home setting.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  <strong>Tip:</strong> If your dog experiences separation anxiety, look for sitters
                  who list &quot;Sitter is home full-time&quot; on their profiles.
                </Trans>
              </FaqParagraph>
            </>
          </FrequentlyAskedQuestion>
        )}

        {/* 7 */}
        <FrequentlyAskedQuestion question={t`Do doggy day cares on Rover have verified reviews?`}>
          <FaqParagraph>
            <Trans>
              Yes, reviews from pet parents who booked doggy day care on Rover are verified. A
              verified review on Rover will list the service the pet parent booked under their name
              and above the review.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>

        {/* 8 - Background check verbiage (North America) */}
        {!hideBackgroundCheckVerbiage && 'whatIsTheStarSitterProgram' in links && (
          <FrequentlyAskedQuestion question={t`How do I book doggy day care?`}>
            <FaqParagraph>
              <Trans>
                Once you've found a sitter whose experience and reviews match your dog's needs,
                click &quot;Contact&quot; to set up a Meet & Greet or trial day. After your Meet &
                Greet, you or your sitter can confirm the booking directly through Rover. If you're
                deciding <Link href={searchUrl}>who to book day care with</Link>, look for markers
                of trust—such as background checks, repeat clients, high ratings, or{' '}
                <Link href={links.whatIsTheStarSitterProgram}>Star Sitter status</Link>.
              </Trans>
            </FaqParagraph>
          </FrequentlyAskedQuestion>
        )}

        {/* 8 - Identity verification verbiage (Europe) */}
        {hideBackgroundCheckVerbiage && 'whatIsTheStarSitterProgram' in links && (
          <FrequentlyAskedQuestion question={t`How do I book doggy day care?`}>
            <FaqParagraph>
              <Trans>
                Once you've found a sitter whose experience and reviews match your dog's needs,
                click &quot;Contact&quot; to set up a Meet & Greet or trial day. After your Meet &
                Greet, you or your sitter can confirm the booking directly through Rover. If you're
                deciding <Link href={searchUrl}>who to book day care with</Link>, look for markers
                of trust—such as identity verification, repeat clients, high ratings, or{' '}
                <Link href={links.whatIsTheStarSitterProgram}>Star Sitter status</Link>.
              </Trans>
            </FaqParagraph>
          </FrequentlyAskedQuestion>
        )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DoggyDayCareServiceFaqs;
